import React, { useState, useRef, useEffect } from "react";
import logo from "../assets/yth-logo1.png";
import { Link } from "react-router-dom";

const TechNavbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Separate states for Desktop and Mobile dropdowns to prevent layout issues when resizing
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside (Applies mainly to Desktop)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDesktopDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Our Team", href: "/ourteams" },
    { name: "Services", href: "/services" },
    { name: "News", href: "/newspage" },
    {
      name: "Departments",
      hasDropdown: true,
      items: [
        { name: "ISP", href: "/isp" },
        { name: "Software Development", href: "/softwaredevelopment" },
        { name: "Graphic Design", href: "/graphicdesign" },
      ],
    },
  ];

  return (
    // Sticky Navbar: Stays at top when scrolling
    <nav className="sticky top-0 left-0 right-0 w-full h-20 z-50 shadow-lg transition-all duration-300 bg-blue-600">
      {/* Navbar Background is now directly on the nav to simplify stacking context */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex items-center justify-between h-full">
          {/* --- Logo Section (Left or Right depending on RTL/LTR) --- */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              {/* FIX 1: Added max-w to prevent logo from taking up too much space on mobile */}
              <div className="h-16 w-auto max-w-[140px] sm:max-w-[180px]">
                <img
                  className="h-full w-full object-contain"
                  src={logo}
                  alt="Yarqin Tech Hub"
                />
              </div>
            </Link>
          </div>

          {/* --- Desktop Menu (Right) --- */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-1 space-x-reverse">
              {navLinks.map((link) => (
                <div
                  key={link.name}
                  className="relative"
                  ref={link.hasDropdown ? dropdownRef : null}
                >
                  {link.hasDropdown ? (
                    // Dropdown Item Wrapper
                    <div>
                      <button
                        onClick={() =>
                          setIsDesktopDropdownOpen(!isDesktopDropdownOpen)
                        }
                        className="text-white hover:bg-white/10 px-4 py-2 rounded-md text-base font-medium flex items-center transition-all duration-200"
                      >
                        {link.name}
                        <svg
                          className={`mr-2 h-4 w-4 transition-transform duration-200 ${
                            isDesktopDropdownOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {/* Dropdown Menu Panel */}
                      {isDesktopDropdownOpen && (
                        <div className="absolute right-0 mt-3 w-56 rounded-lg shadow-2xl bg-blue-700 ring-1 ring-black ring-opacity-5 border border-blue-500 overflow-hidden z-50 transform transition-all origin-top-right">
                          <div className="py-1">
                            {link.items.map((item) => (
                              <a
                                key={item.name}
                                href={item.href}
                                className="block px-4 py-3 text-base text-blue-50 hover:bg-blue-800 hover:text-white transition-colors duration-200"
                              >
                                {item.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Regular Link
                    <a
                      href={link.href}
                      className="text-white hover:bg-white/10 px-4 py-2 rounded-md text-base font-medium transition-all duration-200"
                    >
                      {link.name}
                    </a>
                  )}
                </div>
              ))}

              {/* CTA Button */}
              <Link
                to="/contact"
                className="bg-white text-blue-700 hover:bg-blue-50 px-6 py-2.5 rounded-full text-sm font-bold transition-transform duration-200 hover:scale-105 shadow-md mr-4"
              >
                Contact Us
              </Link>
            </div>
          </div>

          {/* --- Mobile Menu Button (Hamburger) --- */}
          <div className="-mr-2 flex md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-blue-100 hover:text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {!isMobileMenuOpen ? (
                <svg
                  className="block h-7 w-7"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-7 w-7"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* --- Mobile Menu Panel --- */}
      {/* FIX 2: Changed z-40 to z-[60] to ensure it sits above everything, including the parent nav */}
      {/* FIX 3: Changed 'left-0' to 'inset-x-0' to support both RTL and LTR perfectly and cover full width */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-20 inset-x-0 bg-blue-700 border-t border-blue-600 shadow-2xl z-[60] transition-all duration-300 ease-in-out">
          <div className="px-2 pt-2 pb-6 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.hasDropdown ? (
                  // Mobile Dropdown Toggle
                  <div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMobileDropdownOpen(!isMobileDropdownOpen);
                      }}
                      className="w-full text-right text-white hover:bg-blue-800 px-3 py-4 rounded-md text-base font-medium flex justify-between items-center transition-colors"
                    >
                      {link.name}
                      <svg
                        className={`h-4 w-4 transition-transform ${
                          isMobileDropdownOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Mobile Submenu Items */}
                    {isMobileDropdownOpen && (
                      <div className="pl-4 bg-blue-800 border-r-2 border-blue-400 rounded-l-md mt-1 mb-1">
                        {link.items.map((item) => (
                          <a
                            key={item.name}
                            href={item.href}
                            className="text-blue-100 hover:text-white block px-3 py-3 rounded-md text-base font-medium"
                          >
                            {item.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Mobile Regular Link
                  <a
                    href={link.href}
                    className="text-blue-100 hover:text-white hover:bg-blue-800 block px-3 py-4 rounded-md text-base font-medium transition-colors"
                  >
                    {link.name}
                  </a>
                )}
              </div>
            ))}

            {/* Mobile CTA Button */}
            <div className="mt-4 px-3">
              <Link
                to="/contact"
                className="w-full flex justify-center bg-white text-blue-700 hover:bg-blue-50 px-6 py-3 rounded-lg text-sm font-bold shadow-md transition-transform hover:scale-105"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default TechNavbar;
